import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Spinner,
  Textarea,
} from "@magarac/ui";
import { formatCents } from "@magarac/shared/money";
import { invoiceSchema, type Invoice } from "@magarac/shared/schemas/documents";
import { z } from "zod";
import { HelpIcon } from "../../../components/HelpIcon";
import {
  LineItemsEditor,
  draftsToLines,
  toDrafts,
  type LineDraft,
} from "../../../components/LineItemsEditor";
import { api } from "../../../lib/api";
import { INVOICE_STATUS_LABELS, dayText, downloadPdf, statusTone } from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/invoices/$id")({
  component: InvoicePage,
});

const responseSchema = z.object({ invoice: invoiceSchema });

/** CRM-19: one invoice: lines, send, what is paid and owed. Screen key: invoices.edit */
function InvoicePage() {
  const { id } = Route.useParams();
  const { active } = useTenant();
  const q = useQuery({
    queryKey: ["invoice", active?.id, id],
    queryFn: () => api.get(`/invoices/${id}`, responseSchema),
    enabled: Boolean(active),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <Banner tone="danger">{messageFor(q.error)}</Banner>;
  return <InvoiceView key={q.data.invoice.id} invoice={q.data.invoice} />;
}

function InvoiceView({ invoice }: { invoice: Invoice }) {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const voided = invoice.status === "void";
  const [drafts, setDrafts] = useState<LineDraft[]>(() => toDrafts(invoice.lines));
  const [dueOn, setDueOn] = useState(invoice.dueOn ?? "");
  const [notes, setNotes] = useState(invoice.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmVoid, setConfirmVoid] = useState(false);
  const refresh = (data: { invoice: Invoice }) => {
    queryClient.setQueryData(["invoice", active?.id, invoice.id], data);
    void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    void queryClient.invalidateQueries({ queryKey: ["job"] });
  };
  const save = useMutation({
    mutationFn: (body: unknown) => api.patch(`/invoices/${invoice.id}`, body, responseSchema),
    onSuccess: (d) => {
      setNotice("Saved.");
      refresh(d);
    },
    onError: (err) => setError(messageFor(err)),
  });
  const send = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/send`, {}, responseSchema),
    onSuccess: (d) => {
      setNotice("Sent. The customer gets a text (or an email) with the link to pay.");
      refresh(d);
    },
    onError: (err) => setError(messageFor(err)),
  });
  const doVoid = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/void`, {}, responseSchema),
    onSuccess: (d) => {
      setConfirmVoid(false);
      refresh(d);
    },
    onError: (err) => setError(messageFor(err)),
  });
  const doSave = async (thenSend: boolean) => {
    setError(null);
    setNotice(null);
    const parsed = draftsToLines(drafts);
    if ("error" in parsed) return setError(parsed.error);
    const ok = await save
      .mutateAsync({
        lines: parsed.lines,
        dueOn: dueOn || null,
        notes: notes.trim() || null,
        expectedUpdatedAt: invoice.updatedAt,
      })
      .then(() => true)
      .catch(() => false);
    if (ok && thenSend) send.mutate();
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={`Invoice #${invoice.number}`} help={<HelpIcon screen="invoices.edit" />} />
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={statusTone(invoice.status)}>{INVOICE_STATUS_LABELS[invoice.status]}</Badge>
        <Link
          to="/customers/$id"
          params={{ id: invoice.customerId }}
          className="text-brand-700 underline"
        >
          {invoice.customerName}
        </Link>
        <Link to="/jobs/$id" params={{ id: invoice.jobId }} className="text-brand-700 underline">
          Job #{invoice.jobNumber}
        </Link>
        {invoice.dueOn && !voided ? (
          <span className="text-muted">Due {dayText(invoice.dueOn)}</span>
        ) : null}
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      {voided ? (
        <Banner tone="warn">
          Voided {dayText(invoice.voidedAt)}. The customer's link no longer asks for payment.
        </Banner>
      ) : null}
      {invoice.status === "overdue" ? (
        <Banner tone="danger">
          Overdue: {formatCents(invoice.balanceCents)} was due {dayText(invoice.dueOn)}. Send it
          again as a reminder.
        </Banner>
      ) : null}

      <Card className="mb-4">
        <LineItemsEditor
          drafts={drafts}
          onChange={setDrafts}
          taxRateBp={invoice.taxRateBp}
          taxLabel={invoice.taxLabel}
          disabled={voided}
        />
        <dl className="mt-2 ml-auto max-w-xs space-y-1 border-t border-line pt-2 text-sm">
          {invoice.depositsAppliedCents > 0 ? (
            <div className="flex justify-between">
              <dt className="text-muted">Deposit applied</dt>
              <dd className="tabular-nums">-{formatCents(invoice.depositsAppliedCents)}</dd>
            </div>
          ) : null}
          {invoice.paidCents - invoice.depositsAppliedCents > 0 ? (
            <div className="flex justify-between">
              <dt className="text-muted">Paid</dt>
              <dd className="tabular-nums">
                -{formatCents(invoice.paidCents - invoice.depositsAppliedCents)}
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between text-base font-semibold">
            <dt>Balance due</dt>
            <dd className="tabular-nums">{formatCents(invoice.balanceCents)}</dd>
          </div>
        </dl>
        <p className="mt-2 text-sm text-muted">
          Record cash or check on{" "}
          <Link to="/jobs/$id" params={{ id: invoice.jobId }} className="text-brand-700 underline">
            the job
          </Link>
          ; card payments from the customer's link are recorded by themselves.
        </p>
      </Card>
      <Card className="mb-4">
        <Field id="i-due" label="Due date" hint="Set when you send it, from Settings > Invoicing.">
          <Input
            id="i-due"
            type="date"
            value={dueOn}
            disabled={voided}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </Field>
        <Field id="i-notes" label="Note to the customer">
          <Textarea
            id="i-notes"
            rows={3}
            maxLength={2000}
            value={notes}
            disabled={voided}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </Card>

      <div className="flex flex-wrap gap-2">
        {!voided ? (
          <>
            <Button busy={save.isPending || send.isPending} onClick={() => void doSave(true)}>
              {invoice.sentAt ? "Save and send again" : "Save and send"}
            </Button>
            <Button variant="secondary" busy={save.isPending} onClick={() => void doSave(false)}>
              Save
            </Button>
          </>
        ) : null}
        <Button
          variant="secondary"
          onClick={() =>
            void downloadPdf(`/invoices/${invoice.id}/pdf`, `invoice-${invoice.number}.pdf`).catch(
              (e: unknown) => setError(messageFor(e)),
            )
          }
        >
          Download PDF
        </Button>
        {invoice.sentAt && !voided ? (
          <Button
            variant="ghost"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(invoice.url)
                .then(() => setNotice("Customer link copied."))
            }
          >
            Copy customer link
          </Button>
        ) : null}
        {!voided && invoice.paidCents === 0 && !confirmVoid ? (
          <Button variant="ghost" onClick={() => setConfirmVoid(true)}>
            Void
          </Button>
        ) : null}
      </div>
      {confirmVoid ? (
        <Card className="mt-4" title="Void this invoice?">
          <p className="mb-3 text-sm">
            The customer's link stops asking for payment. You can then make a new invoice for this
            job.
          </p>
          <div className="flex gap-2">
            <Button variant="danger" busy={doVoid.isPending} onClick={() => doVoid.mutate()}>
              Void invoice
            </Button>
            <Button variant="secondary" onClick={() => setConfirmVoid(false)}>
              Keep it
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
